/**
 * projectWatcher
 *
 * Reads the active MS Project document's identity (GUID + name) ONCE at pane
 * mount and locks it. We do NOT poll.
 *
 * Identity comes from two sources, in priority order:
 *   1. The in-process sidecar (VSTO add-in / COM sidecar) via the backend
 *      /projects/active endpoint. The sidecar sees MS Project's real focused
 *      project through the COM object model, so its answer is current and is
 *      not subject to the Office.js quirks below.
 *   2. Office.context.document — the fallback when the sidecar has posted
 *      nothing yet or is unreachable. This surface is process-shared across
 *      all open Project windows; polling it caused the pane to drift to
 *      whichever window had focus and reassign chats across documents, which
 *      is why we read it once and lock.
 *
 * The pane is bound to whatever document MS Project had focused at mount. If
 * the user opens a different document in the same window without remounting
 * the pane, the pane stays on its original chat; recovery is via the session
 * dropdown.
 */

/* global Office, console */

import { projectsAPI, chatAPI } from "./api.js";
import { API_CONFIG } from "../config/api.js";
import { getLegacyApiKey } from "../auth/session.js";

// Per-window token injected into this pane's URL by the VSTO add-in when it
// opens the pane via the "Open Shibumi" ribbon button:
//   taskpane.html?window_token=wt-<12hex>
//
// When present, GET /projects/active?window_token=<token> returns the identity
// for this specific Project window rather than the globally-active one, which
// fixes the simultaneous-mount race where two panes mounting at the same
// instant both see the same "last active" snapshot.
//
// When absent (pane opened via the manifest Add-ins menu, no VSTO injection),
// the sidecar falls back to returning the globally-active project — which is
// still correct in the common sequential-mount case.
const _WINDOW_TOKEN = (() => {
  try {
    return new URLSearchParams(window.location.search).get("window_token") || "";
  } catch {
    return "";
  }
})();

export function getProjectWindowToken() {
  // Live value from the watcher: when the visible pane follows a project
  // switch it ADOPTS the newly-active window's token, and send-time snapshot
  // requests must target that window — not the mount-time URL token.
  try {
    return projectWatcher._windowToken || "";
  } catch {
    // Module still initializing (circular import edge) — URL value is correct
    // because no switch can have happened yet.
    return _WINDOW_TOKEN;
  }
}

const UNSAVED_KEY_PREFIX = "unsaved:";
// A sidecar snapshot older than this is treated as dead — the sidecar process
// may have exited — and we fall back to Office.js rather than bind to a stale
// focused project.
const SIDECAR_FRESH_SECONDS = 20;

// How often (ms) the sidecar is polled AFTER the pane has locked, so that
// switching the active MS Project window causes the pane to re-bind.
// Office.js is never polled — its document surface is shared across windows
// and polling it causes chats to drift between documents.
const SWITCH_POLL_MS = 8000;

// GID-143: how often this pane re-asserts its claim on its window_token. Well
// inside the backend's claim TTL, so a couple of dropped requests never cost a
// pane its own window.
const CLAIM_HEARTBEAT_MS = 30000;

/**
 * Per-pane-instance id used to claim a window. Never persisted: task panes
 * share localStorage across every MS Project window under this add-in origin,
 * so a stored id would make both panes the same claimant and defeat the claim.
 */
function _newInstanceId() {
  try {
    return window.crypto.randomUUID();
  } catch {
    return `pane-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
  }
}

class ProjectWatcher {
  constructor() {
    this._current = { guid: null, name: null, identities: [] };
    // How the current identity was obtained: "sidecar" (authoritative) or
    // "office" (the provisional Office.js fallback). An office->sidecar
    // transition is a source upgrade for the same pane, not a project switch.
    this._currentVia = null;
    // Throttle the "snapshot stale" log to the transition into staleness.
    this._loggedStale = false;
    // This pane's per-window token (VSTO panes only). Held on the instance so
    // it is overridable in tests; production value comes from the URL.
    this._windowToken = _WINDOW_TOKEN;
    // GID-143: this pane's claim on _windowToken. Deliberately NOT persisted,
    // for the same reason as _instanceUnsavedKey below — localStorage is shared
    // across every Project window under this add-in origin.
    this._claimed = false;
    this._instanceId = _newInstanceId();
    this._claimHeartbeatTimer = null;
    this._teardownBound = false;
    this._listeners = new Set();
    this._isReading = false;
    this._locked = false;
    this._pollTimer = null;
    this._sse = null;
    this._retryTimers = new Set();
    this._lifecycleGeneration = 0;
    // Per-task-pane-instance synthetic id for unsaved projects with no GUID
    // and no URL. Each MS Project window loads its own task pane iframe →
    // its own ProjectWatcher → its own synthetic id. We deliberately do NOT
    // persist this in localStorage because that storage is shared across
    // all Project windows under the same add-in origin and would re-collide
    // both panes onto the same key when ProjectName/GUID/URL are all empty.
    this._instanceUnsavedKey = null;
    // A background pane skips switch-follow while hidden (it stays pinned to
    // its own window's project). Re-check the moment it becomes visible so a
    // missed switch is picked up immediately instead of on the next poll tick.
    // _checkSidecarForSwitch is lock/generation/isReading-guarded, so a stray
    // event before bind or after account reset is a safe no-op.
    if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          this._checkSidecarForSwitch();
        }
      });
    }
  }

  /**
   * Current { guid, name } pair. null guid means "no project yet read".
   */
  get current() {
    return { guid: this._current.guid, name: this._current.name };
  }

  get discoveryIdentities() {
    return [...(this._current.identities || [])];
  }

  /**
   * Subscribe to project changes. Listener receives ({ guid, name, previous }).
   * Returns an unsubscribe function.
   */
  subscribe(listener) {
    if (typeof listener !== "function") {
      return () => {};
    }
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  /**
   * One-shot: read the bound document's identity and lock it. Safe to call
   * multiple times — subsequent calls are no-ops once locked.
   */
  start() {
    if (this._locked) {
      return;
    }
    this._bindTeardown();
    // Captured before the claim, which is async: if stop() runs while the claim
    // is still in flight, the generation moves on and the continuation below
    // must not start a read for a lifecycle that has already ended.
    const generation = this._lifecycleGeneration;
    // Prime the sidecar so it posts a FRESH snapshot for this window before we
    // read. Post-GID-105 the sidecar only posts on change or request, so at a
    // cold mount (empty ring) there may be nothing to read yet; priming makes
    // the authoritative identity available instead of forcing an Office.js guess.
    //
    // GID-143: the claim runs in the BACKGROUND and the first read does not
    // wait for it.
    //
    // An earlier revision deferred the read until the claim resolved, so that
    // the very first read was already token-scoped. That made the pane's
    // ability to display anything depend on a network round-trip completing:
    // if the identity request hung, the read was never issued and the pane sat
    // empty forever rather than degrading. Nothing bounded that wait.
    //
    // Reading immediately costs only that the first read may be answered
    // globally, for a pane that does not yet know its window. That self-heals:
    // _readWithRetry re-attempts the claim on every attempt, and the
    // office->sidecar path treats the later correction as a source upgrade
    // rather than a project switch, so it does not reset the chat.
    Promise.resolve(this._resolveAndClaimWindow()).catch(() => {});
    this._primeSidecar();
    this._readWithRetry(0, generation);
  }

  /**
   * Bind this pane to one MS Project window and take exclusive ownership of it.
   *
   * A pane can only have been opened in the window that had focus, so the
   * globally-active project's window_token at mount names THIS pane's window.
   * Adopting it is what makes every later read token-scoped, which is the whole
   * fix: an unscoped read answers with whatever window has focus right now, so
   * every pane rebinds to it and two windows end up sharing one project.
   *
   * The claim covers the one case adoption alone cannot: two panes mounting
   * inside the same snapshot window both read the same "last active" token. The
   * backend grants it to one; the loser returns false, keeps no token, and is
   * retried by _readWithRetry — by which time focus has usually moved on and it
   * takes its own window.
   *
   * @returns {Promise<boolean>} true once this pane owns a window token.
   */
  async _resolveAndClaimWindow() {
    if (this._claimed && this._windowToken) return true;

    let token = this._windowToken;
    if (!token) {
      // No token in the pane URL — which is every shipped pane, because the
      // VSTO ribbon button documented as injecting one does not exist. Ask the
      // sidecar which project is active and adopt its window.
      try {
        const payload = await projectsAPI.getActiveProjectIdentity(null);
        token = (payload?.active_project?.window_token || "").trim();
      } catch {
        token = "";
      }
    }
    // Claiming "" would hand every pane the same non-window.
    if (!token) return false;

    let result;
    try {
      result = await projectsAPI.claimWindow(token, this._instanceId);
    } catch {
      return false;
    }
    if (!result || result.granted !== true) {
      console.log(
        "[projectWatcher] window claim refused " +
          JSON.stringify({ token, owner: result?.owner || "?" })
      );
      return false;
    }

    this._windowToken = token;
    this._claimed = true;
    this._startClaimHeartbeat();
    console.log(
      "[projectWatcher] window claimed " +
        JSON.stringify({ token, instance: this._instanceId })
    );
    return true;
  }

  /**
   * Release this pane's window when the pane goes away.
   *
   * Nothing else in the app calls stop(), so without this the release is dead
   * code and the claim TTL is the only way back to a claimable window — which
   * means closing the pane and reopening it in the SAME window inside the TTL
   * gets refused, and that pane degrades to the globally-active behaviour this
   * whole change exists to remove.
   *
   * `pagehide` rather than `unload`: it is the event Chromium (and therefore
   * the WebView2 host) actually fires reliably on teardown.
   */
  _bindTeardown() {
    if (this._teardownBound) return;
    if (typeof window?.addEventListener !== "function") return;
    this._teardownBound = true;
    window.addEventListener("pagehide", () => this.stop());
  }

  /**
   * Re-assert the claim periodically. The backend expires claims because a pane
   * torn down with its MS Project window never gets to release; the heartbeat is
   * what keeps a LIVE pane from expiring out of its own window.
   */
  _startClaimHeartbeat() {
    if (this._claimHeartbeatTimer) return;
    this._claimHeartbeatTimer = setInterval(() => {
      if (!this._claimed || !this._windowToken) return;
      Promise.resolve(
        projectsAPI.claimWindow(this._windowToken, this._instanceId)
      ).catch(() => {});
    }, CLAIM_HEARTBEAT_MS);
  }

  _readWithRetry(attempt = 0, generation = this._lifecycleGeneration) {
    if (generation !== this._lifecycleGeneration) return;
    const isLastAttempt = attempt >= 2;
    this._readActiveProject(isLastAttempt, generation).then(() => {
      if (generation !== this._lifecycleGeneration || this._locked) return;
      this._primeSidecar();
      // GID-143: a pane that lost the claim race retries here. Focus has
      // usually moved on by now, so it adopts its own window instead.
      if (!this._claimed) {
        Promise.resolve(this._resolveAndClaimWindow()).catch(() => {});
      }
      const delay = attempt === 0 ? 1500 : attempt === 1 ? 4000 : null;
      if (delay === null) return;
      const timer = setTimeout(() => {
        this._retryTimers.delete(timer);
        this._readWithRetry(attempt + 1, generation);
      }, delay);
      this._retryTimers.add(timer);
    });
  }

  /**
   * Ask the sidecar to publish a fresh snapshot for this Project window.
   * Best-effort.
   *
   * Deliberately NOT gated on having a window token. A pane opened from the
   * Add-ins menu instead of the ribbon has no token, but the connector is
   * still running: the backend records a token-less request and the sidecar
   * resolves it against the ACTIVE project (see FulfilSnapshotRequest, which
   * only warns for a non-empty token it cannot resolve). Skipping the prime
   * here left the snapshot ring empty for such panes with nothing to refill
   * it — after a backend restart the ring is empty and the sidecar re-posts
   * only when the fingerprint changes, so the pane read 404, fell back to the
   * Office.js namespace, and displayed "Untitled Project" until the user
   * happened to switch documents.
   */
  _primeSidecar() {
    try {
      Promise.resolve(projectsAPI.requestSnapshot(this._windowToken || null)).catch(() => {});
    } catch {
      /* best-effort */
    }
  }

  stop() {
    this._retryTimers.forEach((timer) => clearTimeout(timer));
    this._retryTimers.clear();
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
    if (this._claimHeartbeatTimer) {
      clearInterval(this._claimHeartbeatTimer);
      this._claimHeartbeatTimer = null;
    }
    if (this._sse) {
      this._sse.close();
      this._sse = null;
    }
    // GID-143: hand this window back so a pane reopened here can take it
    // straight away instead of waiting out the claim TTL. Best-effort — a pane
    // torn down with its MS Project window never reaches this at all.
    if (this._claimed && this._windowToken) {
      const token = this._windowToken;
      this._claimed = false;
      Promise.resolve(
        projectsAPI.releaseWindow(token, this._instanceId)
      ).catch(() => {});
    }
  }

  /**
   * Stop account-authenticated transports and forget the bound identity.
   * Listeners remain registered so the same taskpane can start cleanly after
   * another account signs in.
   */
  resetForAccountChange() {
    this._lifecycleGeneration += 1;
    this.stop();
    this._locked = false;
    this._current = { guid: null, name: null, identities: [] };
    this._currentVia = null;
    this._loggedStale = false;
    this._loggedFingerprint = null;
  }

  async _readActiveProject(isLastAttempt = false, generation = this._lifecycleGeneration) {
    if (generation !== this._lifecycleGeneration) {
      return;
    }
    if (this._isReading) {
      return;
    }
    this._isReading = true;

    try {
      // The sidecar is authoritative — it reads THIS window's real project over
      // COM. Office.js is a different, unreliable namespace (a process-global
      // GUID) and is a LAST resort, never the first choice.
      //
      // Token-scoped, deliberately. An earlier GID-143 revision read GLOBALLY
      // here, because otherwise the switch check would spot the new project and
      // this re-read would immediately pull the OLD one back. That is no longer
      // needed: _checkSidecarForSwitch now ADOPTS the newly-active window's
      // token before re-reading, so the token-scoped read already answers for
      // the window the user just switched to.
      const sidecar = await this._readSidecarIdentity();
      let chosen = sidecar;

      if (!sidecar) {
        // Sidecar returned nothing (404). For a VSTO pane the sidecar WILL post
        // once primed, so don't bind the wrong-namespace Office.js identity yet —
        // wait for a retry (that flip is what caused the reset/drift bugs). Fall
        // back to Office.js only as a genuine last resort: a non-VSTO pane (no
        // token, no connector), or a token pane whose retries are exhausted.
        const canWaitForSidecar = Boolean(this._windowToken) && !isLastAttempt;
        if (canWaitForSidecar) {
          return; // stay unlocked; _readWithRetry re-primes and retries
        }
        chosen = await this._readOfficeIdentity();
      }

      if (generation !== this._lifecycleGeneration) {
        return;
      }

      if (!chosen) {
        // Nothing readable yet. Stay unlocked so a later start() can retry.
        return;
      }

      const loggedFingerprint = chosen.stableGuid + "::" + chosen.stableName;
      if (this._loggedFingerprint !== loggedFingerprint) {
        // Stringified so it doesn't collapse in DevTools — paste the
        // whole line back if cross-contamination is being diagnosed.
        console.log(
          "[projectWatcher] identity " +
            JSON.stringify({
              stableGuid: chosen.stableGuid,
              stableName: chosen.stableName,
              identitySource: chosen.identitySource,
              via: sidecar ? "sidecar" : "office-fallback",
            })
        );
        this._loggedFingerprint = loggedFingerprint;
      }

      const identities = Array.isArray(chosen.stableIdentities)
        ? chosen.stableIdentities.filter(Boolean)
        : [chosen.stableGuid].filter(Boolean);
      const identitiesChanged =
        JSON.stringify(identities) !== JSON.stringify(this._current.identities || []);
      const via = sidecar ? "sidecar" : "office";
      if (chosen.stableGuid !== this._current.guid) {
        const previous = { ...this._current };
        this._current = {
          guid: chosen.stableGuid,
          name: chosen.stableName,
          identities,
        };
        this._currentVia = via;
        this._emit({ ...this._current, previous });
      } else if (chosen.stableName !== this._current.name || identitiesChanged) {
        const previous = { ...this._current };
        // Same routing identity, but display data or discovery identities changed.
        this._current = {
          guid: chosen.stableGuid,
          name: chosen.stableName,
          identities,
        };
        this._currentVia = via;
        this._emit({ ...this._current, previous });
      } else {
        this._currentVia = via;
      }

      // First successful read with a real identity → lock this pane to it,
      // then start listening for project-changed SSE events from the backend.
      // SSE fires within ~500ms of a window switch; polling is the fallback.
      if (chosen.stableGuid) {
        this._locked = true;
        if (!this._sse && !this._pollTimer) {
          this._startSSE();
        }
      }
    } catch (err) {
      console.warn("ProjectWatcher: read failed", err);
    } finally {
      this._isReading = false;
    }
  }

  /**
   * Opens a Server-Sent Events connection to GET /projects/events so the pane
   * is notified within ~500ms of a project switch. Falls back to the polling
   * interval if SSE is unavailable or the connection drops.
   */
  _startSSE() {
    // Native EventSource cannot attach the legacy X-Api-Key header. During
    // dual-mode migration, keep project switching on authenticated polling.
    if (getLegacyApiKey()) {
      this._fallbackToPoll();
      return;
    }
    if (typeof EventSource === "undefined") {
      this._fallbackToPoll();
      return;
    }

    const url = `${API_CONFIG.baseURL}/projects/events`;
    const generation = this._lifecycleGeneration;
    let source;
    try {
      source = new EventSource(url, { withCredentials: true });
      this._sse = source;
    } catch {
      this._fallbackToPoll();
      return;
    }

    // Arm the poll NOW, not in onopen/onerror. An EventSource can hang with
    // neither callback ever firing: any hop that buffers the response (a
    // compressing dev-server proxy, a gateway that gzips text/event-stream)
    // holds the headers back, so the browser reports neither "open" nor
    // "error". Starting the poll only from those callbacks made that stall
    // disable switch-following entirely — the pane bound at mount and then
    // never re-checked. Polling already runs alongside a healthy SSE (see
    // onopen below), so arming it here changes nothing when SSE works and is
    // the difference between degraded and dead when it does not.
    this._fallbackToPoll();

    source.onopen = () => {
      if (generation !== this._lifecycleGeneration || this._sse !== source) {
        source.close();
        return;
      }
      console.log("[projectWatcher] SSE connected — real-time project-switch detection active");
      // Keep polling running as a fallback — SSE may "connect" (HTTP 200)
      // but never deliver events (e.g. through Cloudflare tunnels, proxies).
      this._fallbackToPoll();
    };

    source.onmessage = (e) => {
      if (generation !== this._lifecycleGeneration || this._sse !== source) return;
      try {
        const evt = JSON.parse(e.data);
        if (evt.type === "project-changed") {
          console.log("[projectWatcher] SSE project-changed received, name=" + (evt.name || "?"));
          this._checkSidecarForSwitch();
        } else if (evt.type === "auth-expired") {
          this.stop();
          window.dispatchEvent(new CustomEvent("auth-session-invalid"));
        }
      } catch {
        // malformed frame — ignore
      }
    };

    source.onerror = () => {
      if (generation !== this._lifecycleGeneration || this._sse !== source) {
        source.close();
        return;
      }
      console.warn("[projectWatcher] SSE error — falling back to poll");
      source.close();
      this._sse = null;
      this._fallbackToPoll();
    };
  }

  _fallbackToPoll() {
    if (!this._pollTimer) {
      this._pollTimer = setInterval(
        () => this._checkSidecarForSwitch(),
        SWITCH_POLL_MS
      );
    }
  }

  /**
   * Called every SWITCH_POLL_MS after lock. Asks the sidecar if the focused
   * MS Project window has changed. If it has, unlocks and re-reads so the
   * pane rebinds to the new project and switches sessions automatically.
   */
  async _checkSidecarForSwitch() {
    const generation = this._lifecycleGeneration;
    if (this._isReading) return;

    // Two different questions, two different reads:
    //  - A pane still on the provisional Office.js identity needs the answer for
    //    ITS OWN window (token-scoped), so the office->sidecar correction binds
    //    the right project even while another window is active.
    //  - A bound pane needs the GLOBALLY-ACTIVE project: the visible pane must
    //    follow the window the user switched to. The token-scoped read can never
    //    observe such a switch, because a window's own project only changes
    //    identity on Save As — that made ribbon-opened panes permanently stale.
    // Deliberately NOT gated on document.visibilityState: an Office task pane
    // webview does not reliably report itself as visible, so gating on it blocks
    // switch-following entirely in the very host this exists for. There is also
    // no correct local signal to gate on — a token is minted per DOCUMENT
    // (WindowRegistry.KeyFor -> file path), so "my window switched documents"
    // and "another window is active" are indistinguishable from the pane.
    // Following the active project matches the dev-branch behaviour users
    // expect; per-window tokens in the connector are the prerequisite for
    // anything stricter.
    const boundFollow = this._currentVia !== "office";
    if (boundFollow && !this._locked) return;
    let sidecar;
    try {
      sidecar = await this._readSidecarIdentity({ global: boundFollow });
    } catch {
      return;
    }
    if (generation !== this._lifecycleGeneration) return;
    if (!sidecar || !sidecar.stableGuid) return;
    if (sidecar.stableGuid === this._current.guid) return;

    // Office-fallback -> sidecar is a SOURCE UPGRADE for the same pane, not a
    // user switch. The Office.js GUID is a documented-unreliable guess made
    // before the sidecar (the authoritative source for this window_token) had
    // posted; both name the SAME project this pane mounted on. Adopting the
    // sidecar identity must NOT reset the chat the way a real project switch
    // does, or the session the user just created — and any in-flight stream
    // bound to it — is destroyed the instant the sidecar posts. Rebind any
    // sessions filed under the provisional guid, then correct in place.
    if (this._currentVia === "office") {
      const previous = { ...this._current };
      const fromGuid = previous.guid || "";
      const toGuid = sidecar.stableGuid;
      const identities = Array.isArray(sidecar.stableIdentities)
        ? sidecar.stableIdentities.filter(Boolean)
        : [toGuid].filter(Boolean);
      try {
        await chatAPI.rebindProjectGuid(fromGuid, toGuid, sidecar.stableName);
      } catch (err) {
        // Non-fatal: if the rebind fails the session still survives under the
        // provisional guid (reachable via the dropdown). Never block the pane.
        console.warn("[projectWatcher] identity-correction rebind failed", err);
      }
      console.log(
        "[projectWatcher] office->sidecar identity correction " +
          JSON.stringify({ from: fromGuid, to: toGuid })
      );
      this._current = { guid: toGuid, name: sidecar.stableName, identities };
      this._currentVia = "sidecar";
      this._locked = true;
      this._emit({ ...this._current, previous, correction: true });
      return;
    }

    // Save As detection: the synthetic 'sidecar-name:<Project1>' identity
    // for an unsaved doc flips to a 'path:<C:\\...\\.mpp>' identity once
    // MS Project writes the file. Without rebind, chat sessions tagged
    // under the old guid become unreachable and the pane shows an empty
    // greeting. Migrate sessions BEFORE the chat layer hydrates against
    // the new identity.
    const fromGuid = this._current.guid || "";
    const toGuid = sidecar.stableGuid;
    const isSaveAs =
      fromGuid.startsWith("sidecar-name:") && toGuid.startsWith("path:");
    if (isSaveAs) {
      try {
        const result = await chatAPI.rebindProjectGuid(
          fromGuid,
          toGuid,
          sidecar.stableName,
        );
        if (generation !== this._lifecycleGeneration) return;
        console.log(
          "[projectWatcher] Save As detected — rebind " +
            JSON.stringify({
              from: fromGuid,
              to: toGuid,
              rebound: result?.rebound_count ?? 0,
            }),
        );
      } catch (err) {
        // Non-fatal: if rebind fails, user still ends up on the new identity
        // with an empty chat. Better to keep flowing than block the pane.
        console.warn("[projectWatcher] rebind failed", err);
      }
    }

    if (generation !== this._lifecycleGeneration) return;

    console.log(
      "[projectWatcher] project switch detected — re-binding " +
        JSON.stringify({
          from: this._current.guid,
          to: sidecar.stableGuid,
          adoptedWindowToken: sidecar.windowToken || "",
        })
    );
    // Adopt the newly-active window BEFORE re-binding, so the re-bind and every
    // window-scoped operation after it (send-time snapshot requests, task reads,
    // checkpoints) targets the window the user is actually looking at — not the
    // window this pane was originally opened from. Without this, the pane would
    // display the new project but still read/edit the old window's project.
    // Empty token (older connector snapshot) degrades to global-scoped reads,
    // which also prevents a re-bind loop against the stale original token.
    this._windowToken = sidecar.windowToken || "";
    this._locked = false;
    this._readActiveProject(false, generation);
  }

  /**
   * Identity from the in-process sidecar via the backend. Returns null when
   * the sidecar has posted nothing, is unreachable, or its last snapshot is
   * stale — in every case the caller falls back to Office.js.
   */
  async _readSidecarIdentity({ global = false } = {}) {
    let payload;
    try {
      // Identity + freshness only — this read never uses tasks, and it runs at
      // mount and on every switch poll, so it must not pull the whole plan.
      // global=true omits the window token and asks for the GLOBALLY-ACTIVE
      // window's project: switch detection asks "what is the user looking at
      // now?", not "what does this pane's original window hold?".
      payload = await projectsAPI.getActiveProjectIdentity(
        global ? null : this._windowToken || null
      );
    } catch (err) {
      return null;
    }
    if (!payload || !payload.active_project) {
      // No snapshot at all (404). This — not staleness — is the only signal
      // that the sidecar is absent and Office.js should be considered.
      return null;
    }

    // Deliberately DO NOT discard on age. A project's identity (its path/guid)
    // does not change as its snapshot ages; only its TASKS go stale. Post-GID-105
    // there is no heartbeat, so a perfectly-correct identity routinely ages past
    // the freshness cutoff between sends. Discarding it here and falling to
    // Office.js is exactly what made the pane drift to the globally-active
    // project when another window opened. Freshness is surfaced as ageSeconds for
    // callers that genuinely need fresh tasks; identity binding ignores it.
    const age = typeof payload.age_seconds === "number" ? payload.age_seconds : 0;

    const ap = payload.active_project;
    const normalizedGuid = this._normalizeGuid(ap.ms_project_guid);
    const explicitIdentity = (ap.stable_project_identity || "").trim();
    const stableName =
      (ap.display_project_name || "").trim() ||
      (ap.project_name || "").trim() ||
      "Untitled Project";
    // file_full_name is the unique on-disk path of a saved project; file_path
    // is only the containing directory, which sibling files share. Treat the
    // project as on-disk when file_path is non-empty and key off the full path.
    const containingDir = (ap.file_path || "").trim();
    const fullName = (ap.file_full_name || "").trim();
    const isOnDisk = containingDir.length > 0;

    // Identity preference: real GUID > on-disk full path > project name.
    // The name-keyed branch is non-unique for unsaved projects (every blank
    // is "ProjectN"), but it is no worse than the Office.js instance key and
    // saved projects always resolve via the path branch.
    let stableGuid;
    let identitySource;
    // The project GUID is deliberately NOT a candidate for the ROUTING identity,
    // only for the discovery bindings below. The connector derives the routing
    // identity from ProjectGuidResolver.Derive, which knows only path: and
    // sidecar-name: — so preferring a GUID here would make the pane post
    // pending-sync under an identity the connector never looks up, and ribbon
    // Sync would silently do nothing. (This branch was unreachable while the
    // sidecar hardcoded ms_project_guid to "No"; it stops being unreachable the
    // moment that field carries a real GUID.)
    if (explicitIdentity) {
      stableGuid = explicitIdentity;
      identitySource = "sidecar-explicit";
    } else if (isOnDisk && fullName) {
      stableGuid = `path:${fullName}`;
      identitySource = "sidecar-path";
    } else {
      stableGuid = `sidecar-name:${stableName}`;
      identitySource = "sidecar-name";
    }
    const stableIdentities = [
      explicitIdentity,
      normalizedGuid,
      isOnDisk && fullName ? `path:${fullName}` : "",
    ].filter((value, index, values) => value && values.indexOf(value) === index);
    return {
      stableGuid,
      stableName,
      stableIdentities,
      identitySource,
      ageSeconds: age,
      // The active window's token, so a pane that follows a project switch can
      // adopt the window it switched to for all subsequent window-scoped reads.
      windowToken: (ap.window_token || "").trim(),
    };
  }

  /**
   * Identity from Office.context.document. Returns null when the Office
   * Project API is unavailable.
   */
  async _readOfficeIdentity() {
    if (!Office?.context?.document?.getProjectFieldAsync) {
      return null;
    }

    const rawGuid = await this._readField(Office.ProjectProjectFields.GUID);
    const rawName = await this._readField(Office.ProjectProjectFields.ProjectName);
    const rawUrl = Office?.context?.document?.url || "";

    const normalizedGuid = this._normalizeGuid(rawGuid);
    const normalizedUrl = (rawUrl || "").trim();
    const stableName = (rawName || "").trim() || "Untitled Project";
    const isSavedDoc = Boolean(normalizedUrl);

    // Identity preference: real GUID > document URL > per-instance synthetic key.
    // The per-instance synthetic key is a last resort because it resets on
    // every pane mount, which orphans backend sessions the next time the pane
    // reloads on an unsaved-no-GUID document.
    let stableGuid;
    let identitySource;
    if (normalizedGuid) {
      stableGuid = normalizedGuid;
      identitySource = "guid";
    } else if (isSavedDoc) {
      stableGuid = `url:${normalizedUrl}`;
      identitySource = "url";
    } else {
      stableGuid = this._ensureInstanceUnsavedKey();
      identitySource = "instance";
    }
    const stableIdentities = [
      normalizedGuid,
      isSavedDoc ? `url:${normalizedUrl}` : "",
      stableGuid,
    ].filter((value, index, values) => value && values.indexOf(value) === index);
    return { stableGuid, stableName, stableIdentities, identitySource };
  }

  _normalizeGuid(raw) {
    const trimmed = (raw || "").trim();
    if (!trimmed) return "";
    // MS Project returns the literal "No" for the GUID field of a project
    // that has none assigned (unsaved docs). Treat it as absent.
    if (trimmed.toLowerCase() === "no") return "";
    // Strip braces/parens and dashes for the zero check.
    const stripped = trimmed.replace(/[{}\-()]/g, "");
    if (!stripped || /^0+$/.test(stripped)) {
      return "";
    }
    return trimmed;
  }

  _readField(field) {
    return new Promise((resolve) => {
      try {
        Office.context.document.getProjectFieldAsync(field, (result) => {
          if (result?.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value?.fieldValue ?? "");
          } else {
            resolve("");
          }
        });
      } catch (err) {
        resolve("");
      }
    });
  }

  _ensureInstanceUnsavedKey() {
    if (this._instanceUnsavedKey) {
      return this._instanceUnsavedKey;
    }
    const rand = Math.random().toString(36).slice(2, 10);
    this._instanceUnsavedKey = `${UNSAVED_KEY_PREFIX}${Date.now()}-${rand}`;
    return this._instanceUnsavedKey;
  }

  _emit(payload) {
    this._listeners.forEach((listener) => {
      try {
        listener(payload);
      } catch (err) {
        console.error("ProjectWatcher listener threw:", err);
      }
    });
  }
}

export const projectWatcher = new ProjectWatcher();
